import { useCallback, useRef, useState } from "react";

export const useFrameSelection = <T extends { UID: string }>(frames: T[]) => {
    const [selected, setSelected] = useState<string[]>([]);
    const anchorRef = useRef<string | null>(null);

    const selectFrame = useCallback(
        (uid: string, e: React.MouseEvent) => {
            e.stopPropagation();

            const isShift = e.shiftKey;
            const isMeta = e.metaKey || e.ctrlKey;

            setSelected((prev) => {
                if (isShift) {
                    if (!anchorRef.current) {
                        anchorRef.current = uid;
                        return [uid];
                    }

                    const start = frames.findIndex((f) => f.UID === anchorRef.current);
                    const end = frames.findIndex((f) => f.UID === uid);

                    if (start === -1 || end === -1) return prev;

                    const [from, to] = start < end ? [start, end] : [end, start];
                    return frames.slice(from, to + 1).map((f) => f.UID);
                }

                if (isMeta) {
                    return prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid];
                }

                anchorRef.current = uid;
                return [uid];
            });
        },
        [frames],
    );

    const clearSelection = useCallback(() => {
        setSelected([]);
        anchorRef.current = null;
    }, []);

    const isSelected = useCallback((uid: string) => selected.includes(uid), [selected]);

    return {
        selected,
        isSelected,
        selectFrame,
        clearSelection,
        setSelected,
    };
};
